import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Alert, Checkbox, Breadcrumbs } from "@mui/material";
import { Link as RouterLink, useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import useApi from "../../../hooks/useGaneshApi";
import { GridColDef, GridInitialState, GridRenderCellParams } from "@mui/x-data-grid-premium";

import SchemaDiff from "./SchemaDiff";
import { ComplianceIssueBooleanIcon } from "../UI/ComplianceIssueBooleanIcon";
import { useError } from "../../../contexts/ErrorContext";
import { IndividualSchemaSummary_T, SchemaSummaryProps_T } from "../../../types/Schema";

const SchemaCustomerSummary: React.FC<SchemaSummaryProps_T> = ({ customerId, customerName }) => {
    const { customer_id } = useParams<{ customer_id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { apiRequest } = useApi();
    const { showError } = useError();
    
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(customerId || null);
    const [selectedCustomerName, setSelectedCustomerName] = useState<string>(customerName || "");
    const [selectedSchemas, setSelectedSchemas] = useState<string[]>([]);
    const [showDiff, setShowDiff] = useState(false);

    const fetchSchemaSummary = async (customerId: number) => {
        const res = await apiRequest<{ data: IndividualSchemaSummary_T[] }>(`/v1/schemas/customer-schema-summary?customer_id=${customerId}`);
        if (res.data) {
            const dataArray = res.data.data || res.data;
            // Ensure we always have an array and add id field if missing
            const processedData = Array.isArray(dataArray) ? dataArray.map((item, index) => ({
                ...item,
                id: item.id || index + 1
            })) : [];
            return processedData;
        }
        return [];
    };


    const { data: schemaSummary = [], isLoading, error } = useQuery({
        queryKey: ["schemaSummary", selectedCustomerId],
        queryFn: () => selectedCustomerId ? fetchSchemaSummary(selectedCustomerId) : Promise.resolve([]),
        enabled: !!selectedCustomerId,
        staleTime: 120000, // 2 minutes
        refetchInterval: false,
    });

    useEffect(() => {
        if (error) {
            showError(`Failed to fetch schema summary: ${error.message || 'Unknown error'}`);
        }
    }, [error, showError]);

    // Effect to handle URL parameter changes
    useEffect(() => {
        if (customer_id) {
            const id = parseInt(customer_id);
            if (!isNaN(id)) {
                setSelectedCustomerId(id);
                // Update customer name from navigation state
                const stateCustomerName = (location.state as any)?.customerName;
                if (stateCustomerName) {
                    setSelectedCustomerName(stateCustomerName);
                }
            }
        }
    }, [customer_id, location.state]);

    const handleSchemaSelection = (schemaHash: string, isSelected: boolean) => {
        if (isSelected) {
            setSelectedSchemas(prev => [...prev, schemaHash]);
        } else {
            setSelectedSchemas(prev => prev.filter(hash => hash !== schemaHash));
        }
        setShowDiff(false); // Hide diff when selection changes
    };

    const handleCompareSchemas = () => {
        if (selectedSchemas.length === 2) {
            setShowDiff(true);
        }
    };

    const columns_def: GridColDef[] = [
        {
            field: "select",
            headerName: "Select",
            width: 80,
            renderCell: (params: GridRenderCellParams) => (
                <Checkbox
                    checked={selectedSchemas.includes(params.row.encoded_schema_hash)}
                    onChange={(e) => handleSchemaSelection(params.row.encoded_schema_hash, e.target.checked)}
                    disabled={selectedSchemas.length >= 2 && !selectedSchemas.includes(params.row.encoded_schema_hash)}
                />
            ),
            sortable: false,
            filterable: false,
        },
        {
            field: "human_friendly_encoded_schema_hash",
            headerName: "Schema Identifier",
            width: 140,
        },
        {
            field: "delivery_ids",
            headerName: "Deliveries",
            width: 120,
            flex: 1,
            
        },
        {
            field: "delivery_count",
            headerName: "Delivery Count",
            width: 120,
            flex: 1,
        },
        {
            field: "unique_attributes",
            headerName: "Unique Attributes",
            width: 150,
            flex: 1,
        },
        {
            field: "unique_labels",
            headerName: "Unique Labels",
            width: 120,
            flex: 1,
        },
        {
            field: "unique_meta_keys",
            headerName: "Meta Keys",
            width: 100,
            flex: 1,
        },
        {
            field: "unique_meta_values",
            headerName: "Meta Values",
            width: 100,
            flex: 1,
        },
        {
            field: "missing_attributes",
            headerName: "Missing Attributes",
            width: 150,
            flex: 1,
        },
        {
            field: "label_conflicts",
            headerName: "Label Conflicts",
            width: 120,
            flex: 1,
        },
        {
            field: "missing_meta",
            headerName: "Missing Meta",
            width: 120,
            flex: 1,
        },
        {
            field: "meta_conflicts",
            headerName: "Meta Conflicts",
            width: 120,
            flex: 1,
        },
        {
            field: "inconsistent_labels",
            headerName: "Inconsistent Labels",
            width: 120,
            renderCell: ComplianceIssueBooleanIcon,
            flex: 1,
        },
        {
            field: "inconsistent_meta_values",
            headerName: "Inconsistent Meta Values",
            width: 120,
            renderCell: ComplianceIssueBooleanIcon,
            flex: 1,
        },
        {
            field: "is_dominant_schema",
            headerName: "Dominant Schema",
            description: "Schema with the most deliveries",
            width: 120,
            type: "boolean",
            flex: 1,
        }
    ];

    // If no customer ID is provided, show error
    if (!selectedCustomerId && !customer_id) {
        return (
            <Box sx={{ px: 2, py: 1 }}>
                {/* Breadcrumbs for consistency */}
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                    <RouterLink
                        to="/customer-schemas"
                        style={{ textDecoration: "none", color: "inherit" }}
                    >
                        Schema Compliance
                    </RouterLink>
                    <Typography color="text.primary">
                        Customer Schemas
                    </Typography>
                </Breadcrumbs>
                
                <Alert severity="error" sx={{ mb: 2 }}>
                    Invalid customer ID. Please navigate to a valid customer schema page.
                </Alert>
            </Box>
        );
    }

    // Show loading state while processing URL params 
    if (customer_id && !selectedCustomerId) {
        return (
            <Box sx={{ px: 2, py: 1 }}>
                {/* Breadcrumbs for consistency */}
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                    <RouterLink
                        to="/customer-schemas"
                        style={{ textDecoration: "none", color: "inherit" }}
                    >
                        Schema Compliance
                    </RouterLink>
                    <Typography color="text.primary">
                        Customer Schemas
                    </Typography>
                </Breadcrumbs>
                
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <RouterLink
                    to="/customer-schemas"
                    style={{ textDecoration: "none", color: "inherit" }}
                >
                    Schema Compliance
                </RouterLink>
                <Typography color="text.primary">
                    Customer Schemas
                </Typography>
            </Breadcrumbs>

            {/* Compact Header */}
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <Typography variant="h5" component="h2">
                    {selectedCustomerName || `Customer ${selectedCustomerId}`} - Schemas ({schemaSummary.length})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Selection Info - Inline with header */}
                    {selectedSchemas.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {selectedSchemas.length === 1 
                                    ? `1 selected, select 1 more to compare`
                                    : selectedSchemas.length === 2 
                                    ? `2 selected - ready to compare`
                                    : `${selectedSchemas.length} selected`
                                }
                            </Typography>
                            {selectedSchemas.length === 2 && (
                                <>
                                    <Button 
                                        variant="contained" 
                                        color="primary"
                                        onClick={handleCompareSchemas}
                                        size="small"
                                    >
                                        Compare
                                    </Button>
                                    <Button 
                                        variant="outlined"
                                        onClick={() => setSelectedSchemas([])}
                                        size="small"
                                    >
                                        Clear
                                    </Button>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 1, flexShrink: 0 }}>
                    {error.message || 'Failed to fetch schema summary'}
                </Alert>
            )}

            {/* Schema Table - Using compactMode prop */}
            <Box>
                <GaneshDataGrid
                    data={schemaSummary}
                    columns={columns_def}
                    loading={isLoading}
                    compactMode={true}
                    gridId="schema-customer-schemas-grid"
                    disableRowSelectionOnClick={true}
                    disableColumnFilter={false}
                    disableColumnSelector={true}
                    disableDensitySelector={true}
                    isRowSelectable={() => false}
                    sx={{
                        '& .MuiDataGrid-row': {
                            '&.Mui-selected': {
                                backgroundColor: 'transparent !important'
                            },
                            '&.Mui-selected:hover': {
                                backgroundColor: '#f0f0f0 !important'
                            }
                        }
                    }}
                />
            </Box>

            {/* Schema Diff Viewer - Only renders when needed */}
            {showDiff && selectedSchemas.length === 2 ? (
                <Box sx={{ mt: 1 }}>
                    {(() => {
                        const schema1Data = schemaSummary.find(s => s.encoded_schema_hash === selectedSchemas[0]);
                        const schema2Data = schemaSummary.find(s => s.encoded_schema_hash === selectedSchemas[1]);
                        
                        // Parse delivery_ids from string to array, needed because of api response
                        const parseDeliveryIds = (deliveryIds: any): string[] => {
                            if (Array.isArray(deliveryIds)) {
                                return deliveryIds;
                            }
                            if (typeof deliveryIds === 'string') {
                                return deliveryIds.split(',').map(id => id.trim()).filter(Boolean);
                            }
                            return [];
                        };
                        
                        const schema1DeliveryIdsArray = parseDeliveryIds(schema1Data?.delivery_ids);
                        const schema2DeliveryIdsArray = parseDeliveryIds(schema2Data?.delivery_ids);

                        return (
                            <SchemaDiff 
                                customerId={selectedCustomerId!}
                                customerName={selectedCustomerName}
                                schema1Hash={selectedSchemas[0]}
                                schema2Hash={selectedSchemas[1]}
                                schema1DeliveryIds={schema1DeliveryIdsArray}
                                schema2DeliveryIds={schema2DeliveryIdsArray}
                                schema1DeliveryCount={schema1Data?.delivery_count || 0}
                                schema2DeliveryCount={schema2Data?.delivery_count || 0}
                            />
                        );
                    })()}
                </Box>
            ) : null}
        </Box>
    );
};

export default SchemaCustomerSummary;   